import { useEffect, useState } from "react";
import { Search, Moon, Tag, Banknote, Plus, Minus, Paperclip, Users, X } from "lucide-react";
import { Modal } from "../components/Modal";
import { Loader, Spinner } from "../components/Loader";
import { DateField } from "../components/DateField";
import { bookingService } from "../services/bookingService";
import { propertyService } from "../services/propertyService";
import { masterLookupService } from "../services/masterLookupService";
import { addDays, formatDisplayDate, today } from "../utils/date";
import { labelize, money } from "../utils/format";
import { mobileError, sanitizeMobile, MOBILE_LENGTH } from "../utils/validation";

// Guest attachments are converted to multipart files when the guest record is saved.
const MAX_ATTACHMENT_BYTES = 700 * 1024;
const MAX_ATTACHMENT_CHARS = 1000000;
const GUEST_PROOF_ACCEPT = "image/*,application/pdf";

function emptyGuest() {
  return { id: `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, name: "", mobile: "", idType: "", idNumber: "", attachment: null, attachmentName: "" };
}

function additionalGuestLimit(adults) {
  return Math.max(0, Number(adults || 0) - 1);
}

function isPrimaryGuest(guest) {
  return String(guest.guestType || "").toUpperCase() === "PRIMARY";
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error(`Could not read ${file.name}.`));
    reader.readAsDataURL(file);
  });
}

function formatCompactDate(iso) {
  return formatDisplayDate(iso).replace(/ (\w{3})\w* /, "-$1-");
}

function isEditableBooking(status) {
  return ["booked", "on-hold", "on hold", "checked-in"].includes(String(status ?? "").trim().toLowerCase());
}

export function Booking({ notify, tenantId, agentUsername, userId, username }) {
  const [dates, setDates] = useState({ checkIn: today(), checkOut: today(1) });
  const minCheckoutDate = addDays(dates.checkIn, 1);
  const bookingDateRangeIsPast = dates.checkIn < today();
  const bookingDateRangeIsInvalid = dates.checkOut <= dates.checkIn;
  const [rooms, setRooms] = useState([]);
  const [draft, setDraft] = useState(null);
  const [existingGuests, setExistingGuests] = useState([]);
  const [existingGuestsLoaded, setExistingGuestsLoaded] = useState(false);
  const [existingGuestsLoading, setExistingGuestsLoading] = useState(false);
  const [showExistingGuests, setShowExistingGuests] = useState(false);
  const [tenantProperties, setTenantProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [idTypeOptions, setIdTypeOptions] = useState([]);
  const [confirmationMessage, setConfirmationMessage] = useState("");

  useEffect(() => {
    masterLookupService.getByCategory("ID_TYPE")
      .then((data) => setIdTypeOptions(data.map((item) => item.displayName)))
      .catch(() => setIdTypeOptions(["Aadhaar", "Passport", "Driving License", "Voter ID", "PAN Card", "Other"]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (agentUsername) {
      bookingService.getAvailability(dates.checkIn, dates.checkOut, agentUsername)
        .then((results) => { if (!cancelled) { setTenantProperties([]); setRooms(results); } })
        .catch(() => { if (!cancelled) { setTenantProperties([]); setRooms([]); } })
        .finally(() => { if (!cancelled) setLoading(false); });
      return () => { cancelled = true; };
    }
    Promise.all([
      propertyService.listForTenant(tenantId).then((list) => list.map((p) => p.name)).catch(() => []),
      bookingService.getAvailability(dates.checkIn, dates.checkOut, null, tenantId).catch(() => []),
    ]).then(([names, results]) => {
      if (cancelled) return;
      setTenantProperties(names);
      setRooms(names.length ? results.filter((room) => names.includes(room.propertyName)) : []);
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [tenantId, agentUsername]);

  async function search() {
    setLoading(true);
    try {
      const results = await bookingService.getAvailability(dates.checkIn, dates.checkOut, agentUsername, tenantId);
      if (agentUsername) {
        setRooms(results);
        return;
      }
      setRooms(tenantProperties.length ? results.filter((room) => tenantProperties.includes(room.propertyName)) : []);
    } catch (err) {
      console.error("Booking availability search error:", err);
      notify(err?.message || "Could not load availability.");
    } finally {
      setLoading(false);
    }
  }

  async function loadExistingGuests() {
    if (existingGuestsLoaded) return existingGuests;
    if (existingGuestsLoading) return null;

    setExistingGuestsLoading(true);
    try {
      const guests = await bookingService.getGuests(draft.id);
      storeExistingGuests(guests, draft.id);
      return guests;
    } catch (err) {
      console.error("Booking load guests error:", err);
      notify(err?.message || "Could not load guest details.");
      return null;
    } finally {
      setExistingGuestsLoading(false);
    }
  }

  function validateDraft(current, existingAdditionalGuestCount = 0, primaryGuest = null) {
    const next = {};
    const guests = current.guests || [];
    const additionalGuestsCount = existingAdditionalGuestCount + guests.length;
    const expectedAdditionalGuests = Math.max(additionalGuestLimit(current.adults), existingAdditionalGuestCount);

    if (additionalGuestsCount !== expectedAdditionalGuests) {
      next.guestsCountMismatch = `Add guest details for each additional adult. This booking requires ${expectedAdditionalGuests} additional guest details (one per adult after the primary guest).`;
    }

    if (!current.guestName?.trim()) {
      next.guestName = "Guest name is required.";
    }

    const contactError = mobileError(current.contact, "Contact number");
    if (contactError) next.contact = contactError;

    if (!current.primaryIdType && !primaryGuest?.idType) {
      next.primaryIdType = "ID type is required.";
    }
    if (!current.govtId?.trim() && !primaryGuest?.idNumber?.trim()) {
      next.govtId = "ID number is required.";
    }
    if (!current.primaryProof && !current.primaryProofExisting && !primaryGuest?.image && !primaryGuest?.attachment) {
      next.primaryProof = "ID proof is required.";
    }

    guests.forEach((guest, index) => {
      if (!guest.name.trim()) next[`guest.${index}.name`] = "Guest name is required.";
      if (!guest.mobile) next[`guest.${index}.mobile`] = "Mobile number is required.";
      else {
        const error = mobileError(guest.mobile);
        if (error) next[`guest.${index}.mobile`] = error;
      }
      if (!guest.idType) next[`guest.${index}.idType`] = "ID type is required.";
      if (!guest.idNumber?.trim()) next[`guest.${index}.idNumber`] = "ID number is required.";
      if (!guest.attachment) next[`guest.${index}.attachment`] = "ID proof is required.";
    });

    return { errors: next, guests };
  }

  async function saveBooking(event, statusConfirmed = false) {
    event?.preventDefault();
    if (saving) return;
    if (!userId) {
      notify("Could not identify the logged-in user. Please sign in again.");
      return;
    }

    setSaving(true);
    try {
      let loadedGuests = existingGuests;
      if (draft.id) {
        if (!existingGuestsLoaded) {
          loadedGuests = await bookingService.getGuests(draft.id);
          storeExistingGuests(loadedGuests, draft.id);
        }
      }

      const primaryGuest = loadedGuests.find(isPrimaryGuest) || null;
      const existingAdditionalGuestCount = loadedGuests.filter((guest) => !isPrimaryGuest(guest)).length;
      const { errors: found, guests } = validateDraft(draft, existingAdditionalGuestCount, primaryGuest);
      setErrors(found);
      if (found.guestsCountMismatch) {
        notify(found.guestsCountMismatch);
        return;
      }
      if (Object.keys(found).length) {
        notify(Object.values(found)[0]);
        return;
      }

      const statusConfirmation = {
        "Checked-in": "Confirm marking this booking as checked in?",
        "Checked-Out": "Confirm marking this booking as checked out?",
      }[draft.status];
      if (statusConfirmation && !statusConfirmed) {
        setConfirmationMessage(statusConfirmation);
        return;
      }

      const savedBooking = await bookingService.saveBooking({
        ...draft,
        bookedBy: draft.id ? draft.bookedBy : userId,
        modifiedBy: userId,
        contact: sanitizeMobile(draft.contact),
        govtId: draft.govtId || primaryGuest?.idNumber || "",
      });
      const bookingId = savedBooking?.id || savedBooking?.bookingId || draft.id;
      if (!bookingId) {
        throw new Error("Booking was saved, but no booking ID was returned for guest details.");
      }
      const guestSaveRequests = [];
      if (!primaryGuest) {
        guestSaveRequests.push(
          bookingService.createGuest({
            bookingId: String(bookingId),
            guestType: "PRIMARY",
            name: draft.guestName.trim(),
            mobile: sanitizeMobile(draft.contact),
            idType: draft.primaryIdType,
            idNumber: draft.govtId?.trim(),
            attachment: draft.primaryProof,
            attachmentName: draft.primaryProofName,
          })
        );
      }
      guestSaveRequests.push(...guests.map((guest) =>
        bookingService.createGuest({
          bookingId: String(bookingId),
          name: guest.name.trim(),
          mobile: sanitizeMobile(guest.mobile),
          idType: guest.idType,
          idNumber: guest.idNumber?.trim(),
          attachment: guest.attachment,
          attachmentName: guest.attachmentName,
        })
      ));
      await Promise.all(guestSaveRequests);
      closeDraft();
      setErrors({});
      setConfirmationMessage("");
      notify("Booking saved successfully.");
      await search();
    } catch (err) {
      console.error("Booking save error:", err);
      notify(err?.message || "Could not save booking.");
    } finally {
      setSaving(false);
    }
  }

  function bookingTotal(adults, rate) {
    return Number(adults || 0) * Number(rate || 0);
  }

  function updateNumberField(key, value) {
    setDraft((current) => {
      const next = { ...current, [key]: value };

      if (key === "adults" || key === "agreedPrice") {
        next.totalAmount = bookingTotal(next.adults, next.agreedPrice);
        next.remainingBalance = next.totalAmount - Number(next.advancePaid || 0);
      }

      return next;
    });
  }

  async function addGuest() {
    let existingAdditionalGuestCount = existingGuests.filter((guest) => !isPrimaryGuest(guest)).length;
    if (draft.id) {
      const guests = await loadExistingGuests();
      if (!guests) return;
      existingAdditionalGuestCount = guests.filter((guest) => !isPrimaryGuest(guest)).length;
    }

    if (existingAdditionalGuestCount + (draft.guests || []).length >= additionalGuestLimit(draft.adults)) {
      notify("Additional guest details cannot exceed the number of adults after the primary guest.");
      return;
    }

    setDraft((current) => {
      const guests = current.guests || [];
      if (existingAdditionalGuestCount + guests.length >= additionalGuestLimit(current.adults)) {
        return current;
      }
      return { ...current, guests: [...guests, emptyGuest()] };
    });
  }

  function removeGuest(index) {
    setDraft((current) => ({ ...current, guests: (current.guests || []).filter((_, i) => i !== index) }));
  }

  function clearGuests() {
    setDraft((current) => ({ ...current, guests: [] }));
  }

  function updateGuest(index, patch) {
    setDraft((current) => ({
      ...current,
      guests: (current.guests || []).map((guest, i) => (i === index ? { ...guest, ...patch } : guest)),
    }));
  }

  async function attachGuest(index, fileList) {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      notify("Guest proof must be an image or PDF file.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      notify(`${file.name} is larger than ${MAX_ATTACHMENT_BYTES / 1024} KB.`);
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      if (dataUrl.length > MAX_ATTACHMENT_CHARS) {
        notify(`${file.name} is too detailed to store; try a smaller file.`);
        return;
      }
      updateGuest(index, { attachment: dataUrl, attachmentName: file.name });
    } catch (err) {
      console.error("Booking attach guest proof error:", err);
      notify(err?.message || "Could not attach that file.");
    }
  }

  async function attachPrimaryProof(fileList) {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    if (!file.type.startsWith("image/") && file.type !== "application/pdf") {
      notify("Guest proof must be an image or PDF file.");
      return;
    }
    if (file.size > MAX_ATTACHMENT_BYTES) {
      notify(`${file.name} is larger than ${MAX_ATTACHMENT_BYTES / 1024} KB.`);
      return;
    }
    try {
      const dataUrl = await readAsDataUrl(file);
      if (dataUrl.length > MAX_ATTACHMENT_CHARS) {
        notify(`${file.name} is too detailed to store; try a smaller file.`);
        return;
      }
      setDraft((current) => ({ ...current, primaryProof: dataUrl, primaryProofName: file.name, primaryProofExisting: false }));
      setErrors((current) => ({ ...current, primaryProof: undefined }));
    } catch (err) {
      console.error("Booking attach primary proof error:", err);
      notify(err?.message || "Could not attach that file.");
    }
  }

  function storeExistingGuests(guests, bookingId) {
    const primaryGuest = guests.find(isPrimaryGuest) || null;
    setExistingGuests(guests);
    setExistingGuestsLoaded(true);
    setDraft((current) => {
      if (current?.id !== bookingId || !primaryGuest) return current;
      return {
        ...current,
        primaryIdType: current.primaryIdType || primaryGuest.idType || "",
        govtId: current.govtId || primaryGuest.idNumber || "",
        primaryProofExisting: Boolean(primaryGuest.image || primaryGuest.attachment),
        primaryProofName: current.primaryProofName || (primaryGuest.image || primaryGuest.attachment ? "Existing ID proof" : ""),
      };
    });
  }

  function openDraft(room) {
    const existingBooking = isEditableBooking(room.status);
    if (existingBooking && !room.bookingId) {
      notify("Cannot edit this booking because its booking ID is missing.");
      return;
    }
    const adults = room.adults || 1;
    const agreedPrice = room.agreedPrice || room.price;
    const totalAmount = bookingTotal(adults, agreedPrice);

    setErrors({});
    setExistingGuests([]);
    setExistingGuestsLoaded(false);
    setExistingGuestsLoading(false);
    setShowExistingGuests(false);

    setDraft({
      id: room.bookingId || "",
      roomId: room.id == null ? "" : String(room.id),
      propertyName: room.propertyName,
      roomName: room.roomName,
      checkInDate: room.checkInDate || dates.checkIn,
      checkOutDate: room.checkOutDate || dates.checkOut,
      guestName: room.guestName || "",
      contact: room.contact || "",
      govtId: room.govtId || "",
      primaryIdType: "",
      primaryProof: null,
      primaryProofName: "",
      primaryProofExisting: false,
      adults,
      kids: room.kids || 0,
      status: room.status === "Available" ? "On-hold" : room.status,
      totalAmount,
      agreedPrice,
      advancePaid: room.advancePaid || 0,
      remainingBalance: totalAmount - Number(room.advancePaid || 0),
      paymentMode: room.paymentMode || "UPI",
      travellingCity: room.travellingCity || "",
      vehicleNumber: room.vehicleNumber || "",
      guests: [],
      bookedBy: room.bookedBy || "",
    });
    if (room.bookingId) {
      setExistingGuestsLoading(true);
      bookingService.getGuests(room.bookingId)
        .then((guests) => storeExistingGuests(guests, room.bookingId))
        .catch((err) => notify(err?.message || "Could not load guest details."))
        .finally(() => setExistingGuestsLoading(false));
    }
  }

  async function toggleExistingGuests() {
    if (showExistingGuests) {
      setShowExistingGuests(false);
      return;
    }

    setShowExistingGuests(true);
    if (existingGuestsLoaded || existingGuestsLoading) return;

    setExistingGuestsLoading(true);
    try {
      const guests = await bookingService.getGuests(draft.id);
      storeExistingGuests(guests, draft.id);
    } catch (err) {
      console.error("Booking load existing guests error:", err);
      setShowExistingGuests(false);
      notify(err?.message || "Could not load guest details.");
    } finally {
      setExistingGuestsLoading(false);
    }
  }

  function closeDraft() {
    setDraft(null);
    setExistingGuests([]);
    setExistingGuestsLoaded(false);
    setShowExistingGuests(false);
  }

  return (
    <section className="stack">
      <div className="toolbar">
        <label className="date-label">
          Check-in
          <DateField value={dates.checkIn} onChange={(value) => setDates({ ...dates, checkIn: value })} showWeekday ariaLabel="Check-in date" />
        </label>
        <label className="date-label">
          Check-out
          <DateField value={dates.checkOut} onChange={(value) => setDates({ ...dates, checkOut: value })} showWeekday ariaLabel="Check-out date" min={minCheckoutDate} />
        </label>
        <button className="primary" disabled={loading} onClick={search}>
          {loading ? <Spinner size={15} /> : <Search size={16} />}
          {loading ? "Loading..." : "Search Availability"}
        </button>
      </div>
      <div className="room-grid">
        {loading ? (
          <Loader label="Checking availability..." className="room-grid-loading" />
        ) : rooms.length === 0 ? (
          <div className="table-empty">No rooms available for these dates.</div>
        ) : rooms.map((room) => (
          <article className="room-card" key={room.id}>
            <div className="room-card-header">
              <div className="room-card-header-left">
                <span className="room-resort">{room.propertyName}</span>
                <span className="room-type">{room.roomName}</span>
              </div>
              <div className="room-card-header-right">
                <span className="detail-label">Per Adult</span>
                <span className="room-price">{money(room.price)}</span>
              </div>
            </div>
            <div className="room-card-body">
              <div className="room-card-detail">
                <span className="detail-label">Room Type</span>
                <span className="detail-value room-type-value">{room.roomType}</span>
              </div>
              <div className="room-card-detail">
                <span className="detail-label">Bed Type</span>
                <span className="detail-value">{room.bedType}</span>
              </div>
              <div className="room-card-detail">
                <span className="detail-label">Max Occupancy</span>
                <span className="detail-value">{room.maxOccupancy} guests</span>
              </div>
            </div>
            <div className="room-card-footer">
              <div className="room-card-status-group">
                <span className={`status ${room.status}`}>{room.status}</span>
                {isEditableBooking(room.status) && (
                  <span className="room-card-dates">
                    Check-in: {formatCompactDate(room.checkInDate || dates.checkIn)} · Check-out: {formatCompactDate(room.checkOutDate || dates.checkOut)}
                  </span>
                )}
              </div>
              <button
                className="primary"
                disabled={!isEditableBooking(room.status) && (room.status !== "Available" || bookingDateRangeIsPast || bookingDateRangeIsInvalid)}
                onClick={() => openDraft(room)}
                title={isEditableBooking(room.status) ? "Edit booking" : bookingDateRangeIsInvalid ? "Check-out must be after check-in" : bookingDateRangeIsPast ? "Select a current or future date range to book" : room.status === "Available" ? "Book now" : "Room is not available"}
              >
                {isEditableBooking(room.status) ? "Edit Booking" : "Book Now"}
              </button>
            </div>
          </article>
        ))}
      </div>
      {draft && (
        <Modal title={draft.id ? "Edit Booking" : "Create New Reservation"} headerStyle={{ background: "#1d4ed8", color: "#fff", margin: "-18px -18px 0 -18px", padding: "16px 18px", borderRadius: "8px 8px 0 0" }} onClose={closeDraft}
          fixedHeader={
            <section style={{ background: "#1d4ed8", padding: "16px 0", display: "flex", gap: "8px", fontSize: "14px", alignItems: "start", margin: "0 -18px", marginBottom: "16px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "6px 16px", flex: "0.98 0.98 0.98%" }}>
                <div style={{ color: "#ffffff", fontSize: "0.75rem", paddingLeft: "16px" }}><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Property:</span> <span style={{ fontWeight: "500" }}>{draft.propertyName}</span></div>
                <div style={{ color: "#ffffff", fontSize: "0.75rem" }}><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Room:</span> <span style={{ fontWeight: "500" }}>{draft.roomName}</span></div>
                <div style={{ color: "#ffffff", fontSize: "0.75rem", paddingLeft: "16px" }}><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Check-in:</span> <span style={{ fontWeight: "500" }}>{formatDisplayDate(draft.checkInDate, { weekday: true })}</span></div>
                <div style={{ color: "#ffffff", fontSize: "0.75rem" }}><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Check-out:</span> <span style={{ fontWeight: "500" }}>{formatDisplayDate(draft.checkOutDate, { weekday: true })}</span></div>
                <div style={{ color: "#ffffff", fontSize: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "6px", paddingLeft: "16px" }}><Moon size={14} style={{ verticalAlign: "middle", marginRight: "4px", color: "#facc15" }} /><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Nights:</span> <span style={{ color: "#facc15", fontWeight: "500" }}>{Math.ceil((new Date(draft.checkOutDate) - new Date(draft.checkInDate)) / (1000 * 60 * 60 * 24))}</span></div>
                <div style={{ color: "#ffffff", fontSize: "0.75rem", borderTop: "1px solid rgba(255,255,255,0.2)", paddingTop: "6px" }}><Tag size={14} style={{ verticalAlign: "middle", marginRight: "4px", color: "#facc15" }} /><span style={{ color: "#bfdbfe", letterSpacing: "0.06em", fontWeight: "700", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Rate:</span> <span style={{ color: "#facc15", fontWeight: "500" }}>{money(draft.agreedPrice)}</span></div>
              </div>
              <div style={{ background: "#1e40af", color: "#ffffff", padding: "16px 16px 16px 16px", borderRadius: "8px", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "flex-start", gap: "8px", flexShrink: 0 }}>
                <span style={{ fontSize: "9px", fontWeight: "700", letterSpacing: "0.06em", color: "#bfdbfe", fontFamily: "ui-sans-serif, system-ui, sans-serif, \"Apple Color Emoji\", \"Segoe UI Emoji\", \"Segoe UI Symbol\", \"Noto Color Emoji\"" }}>Total Amount</span>
                <strong style={{ color: "#facc15", fontSize: "1.25rem", fontWeight: "900", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, \"Liberation Mono\", \"Courier New\", monospace" }}>{money(draft.totalAmount)}</strong>
              </div>
            </section>
          }
          footer={
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", background: "#f1f5f9", margin: "0 -18px -18px -18px", padding: "14px 18px", borderRadius: "0 0 8px 8px" }}>
              <button type="button" className="secondary" disabled={saving} onClick={closeDraft}>Cancel</button>
              <button type="submit" className="primary" disabled={saving} form="booking-form">
                {saving && <Spinner size={14} />}
                {saving ? "Saving..." : draft.id ? "Save Changes" : "Save Booking"}
              </button>
            </div>
          }>
          <form id="booking-form" className="form-grid" onSubmit={saveBooking} aria-busy={saving || undefined}>
            <section className="wide booking-section">
              <h3 className="booking-section-title" style={{ color: "#1d4ed8" }}>Guest Particulars</h3>
              <div className="form-grid guest-fields">
                {["adults", "kids"].map((key) => (
                <label key={key}>
                  {labelize(key)}
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={draft[key] || 0}
                    onChange={(event) => updateNumberField(key, Math.max(0, Number(event.target.value) || 0))}
                  />
                </label>
              ))}
                {["guestName", "contact", "primaryIdType", "govtId", "primaryProof", "travellingCity", "vehicleNumber"].map((key) => {
                  if (key === "primaryProof") {
                    return (
                      <div className="guest-attachment" key={key}>
                        <span className="guest-attachment-label">ID Proof <span style={{ color: "#dc2626" }}>*</span></span>
                        <div className="guest-attachment-picker">
                          <label className="secondary attachment-button" title={draft.primaryProofName || "Choose a file"}>
                            <Paperclip size={15} />
                            <span className="attachment-name">{draft.primaryProofName || "Choose file"}</span>
                            <input
                              type="file"
                              accept={GUEST_PROOF_ACCEPT}
                              hidden
                              onChange={(event) => attachPrimaryProof(event.target.files)}
                            />
                          </label>
                          {draft.primaryProof ? (
                            <button
                              type="button"
                              className="row-icon-btn is-danger"
                              title="Remove attachment"
                              onClick={() => setDraft((current) => ({ ...current, primaryProof: null, primaryProofName: "", primaryProofExisting: false }))}
                            >
                              <X size={14} />
                            </button>
                          ) : null}
                        </div>
                        {errors.primaryProof ? <span className="field-error">{errors.primaryProof}</span> : null}
                      </div>
                    );
                  }

                  return (
                    <label key={key}>
                      <span>
                        {key === "govtId" ? "ID Number" : key === "primaryIdType" ? "ID Type" : labelize(key)}
                        {["guestName", "contact", "govtId", "primaryIdType"].includes(key) && (
                          <span style={{ color: "#dc2626" }}>{key === "primaryIdType" ? "*" : " *"}</span>
                        )}
                      </span>
                      {key === "guestName" ? (
                        <>
                          <input
                            placeholder="Guest Name"
                            aria-invalid={errors.guestName ? "true" : undefined}
                            aria-describedby={errors.guestName ? "guest-name-error" : undefined}
                            value={draft.guestName || ""}
                            onChange={(event) => {
                              setDraft({ ...draft, guestName: event.target.value });
                              setErrors((current) => ({ ...current, guestName: undefined }));
                            }}
                            onBlur={() =>
                              setErrors((current) => ({
                                ...current,
                                guestName: draft.guestName?.trim() ? undefined : "Guest name is required.",
                              }))
                            }
                          />
                          {errors.guestName ? <span className="field-error" id="guest-name-error">{errors.guestName}</span> : null}
                        </>
                      ) : key === "contact" ? (
                        <>
                          <input
                            type="tel"
                            inputMode="numeric"
                            autoComplete="tel"
                            maxLength={MOBILE_LENGTH}
                            placeholder={`${MOBILE_LENGTH}-digit mobile number`}
                            aria-invalid={errors.contact ? "true" : undefined}
                            aria-describedby={errors.contact ? "contact-error" : undefined}
                            value={draft[key] || ""}
                            onChange={(event) => {
                              const value = sanitizeMobile(event.target.value);
                              setDraft({ ...draft, [key]: value });
                              setErrors((current) => ({ ...current, [key]: undefined }));
                            }}
                            onBlur={() =>
                              setErrors((current) => ({ ...current, [key]: draft[key] ? mobileError(draft[key], "Contact number") : undefined }))
                            }
                          />
                          {errors.contact ? <span className="field-error" id="contact-error">{errors.contact}</span> : null}
                        </>
                      ) : key === "primaryIdType" ? (
                        <>
                          <select
                            value={draft.primaryIdType || ""}
                            aria-invalid={errors.primaryIdType ? "true" : undefined}
                            onChange={(event) => {
                              setDraft({ ...draft, primaryIdType: event.target.value });
                              setErrors((current) => ({ ...current, primaryIdType: undefined }));
                            }}
                          >
                            <option value="">Select</option>
                            {idTypeOptions.map((type) => (
                              <option key={type} value={type}>{type}</option>
                            ))}
                          </select>
                          {errors.primaryIdType ? <span className="field-error">{errors.primaryIdType}</span> : null}
                        </>
                      ) : key === "govtId" ? (
                        <>
                          <input
                            placeholder="ID number"
                            aria-invalid={errors.govtId ? "true" : undefined}
                            value={draft.govtId || ""}
                            onChange={(event) => {
                              setDraft({ ...draft, govtId: event.target.value });
                              setErrors((current) => ({ ...current, govtId: undefined }));
                            }}
                          />
                          {errors.govtId ? <span className="field-error">{errors.govtId}</span> : null}
                        </>
                      ) : (
                        <input
                          placeholder={labelize(key)}
                          value={draft[key] || ""}
                          onChange={(event) => setDraft({ ...draft, [key]: event.target.value })}
                        />
                      )}
                    </label>
                  );
                })}

                <div className="wide guest-actions">
                  {draft.id ? (
                    <>
                      <button
                        type="button"
                        className="secondary"
                        onClick={addGuest}
                        disabled={existingGuestsLoading || existingGuests.filter((guest) => !isPrimaryGuest(guest)).length + (draft.guests || []).length >= additionalGuestLimit(draft.adults)}
                        title={additionalGuestLimit(draft.adults) <= existingGuests.filter((guest) => !isPrimaryGuest(guest)).length + (draft.guests || []).length ? "Additional guest details cannot exceed adults minus one" : "Add guest details for an additional adult"}
                      >
                        {existingGuestsLoading ? <Spinner size={12} /> : <Plus size={13} />}
                        {existingGuestsLoading ? "Loading" : "Add Guest Details"}
                      </button>
                      <button type="button" className="secondary guest-list-toggle" onClick={toggleExistingGuests} disabled={existingGuestsLoading} aria-expanded={showExistingGuests}>
                        {existingGuestsLoading ? <Spinner size={12} /> : <Users size={13} />}
                        {existingGuestsLoading ? "Loading" : showExistingGuests ? "Hide Guests" : "View Guests"}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="secondary"
                        onClick={addGuest}
                        disabled={additionalGuestLimit(draft.adults) <= (draft.guests || []).length}
                        title={additionalGuestLimit(draft.adults) <= (draft.guests || []).length ? "Additional guest details cannot exceed adults minus one" : "Add guest details"}
                      >
                        Add Guest Details
                      </button>
                      <button type="button" className="secondary" disabled={!(draft.guests || []).length} onClick={clearGuests}>Cancel</button>
                    </>
                  )}
                </div>

                {draft.id && showExistingGuests && (
                  <div className="wide existing-guests-list" aria-live="polite">
                    {existingGuests.filter((guest) => !isPrimaryGuest(guest)).length === 0 ? (
                      <span className="detail-label">No guest details found for this booking.</span>
                    ) : existingGuests.filter((guest) => !isPrimaryGuest(guest)).map((guest, index) => (
                      <div className="existing-guest-row" key={guest.id}>
                        <strong>Guest {index + 2}: {guest.name}</strong>
                        <span>{guest.mobile || "No mobile"} · {guest.idType || "No ID type"}{guest.idNumber ? `: ${guest.idNumber}` : ""}</span>
                      </div>
                    ))}
                  </div>
                )}

                {(draft.guests || []).map((guest, index) => (
                  <div className="wide guest-row" key={guest.id}>
                    <span className="guest-row-title">Guest {index + (draft.id ? existingGuests.filter((item) => !isPrimaryGuest(item)).length : 0) + 2}</span>
                    <div className="guest-row-grid">
                      <div className="guest-row-fields">
                      <label>
                        Name<span style={{ color: "#dc2626" }}>*</span>
                        <input
                          placeholder="Guest name"
                          aria-invalid={errors[`guest.${index}.name`] ? "true" : undefined}
                          aria-describedby={errors[`guest.${index}.name`] ? `guest-${index}-name-error` : undefined}
                          value={guest.name}
                          onChange={(event) => {
                            updateGuest(index, { name: event.target.value });
                            setErrors((current) => ({ ...current, [`guest.${index}.name`]: undefined }));
                          }}
                          onBlur={() =>
                            setErrors((current) => ({
                              ...current,
                              [`guest.${index}.name`]: guest.name.trim() ? undefined : "Guest name is required.",
                            }))
                          }
                        />
                        {errors[`guest.${index}.name`] ? (
                          <span className="field-error" id={`guest-${index}-name-error`}>{errors[`guest.${index}.name`]}</span>
                        ) : null}
                      </label>
                      <label>
                        Mobile<span style={{ color: "#dc2626" }}>*</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          autoComplete="tel"
                          maxLength={MOBILE_LENGTH}
                          placeholder={`${MOBILE_LENGTH}-digit mobile number`}
                          aria-invalid={errors[`guest.${index}.mobile`] ? "true" : undefined}
                          aria-describedby={errors[`guest.${index}.mobile`] ? `guest-${index}-mobile-error` : undefined}
                          value={guest.mobile}
                          onChange={(event) => {
                            const value = sanitizeMobile(event.target.value);
                            updateGuest(index, { mobile: value });
                            setErrors((current) => ({ ...current, [`guest.${index}.mobile`]: undefined }));
                          }}
                          onBlur={() =>
                            setErrors((current) => ({
                              ...current,
                              [`guest.${index}.mobile`]: guest.mobile ? mobileError(guest.mobile) : "Mobile number is required.",
                            }))
                          }
                        />
                        {errors[`guest.${index}.mobile`] ? (
                          <span className="field-error" id={`guest-${index}-mobile-error`}>{errors[`guest.${index}.mobile`]}</span>
                        ) : null}
                      </label>
                      <label>
                        ID Type<span style={{ color: "#dc2626" }}>*</span>
                        <select
                          value={guest.idType || ""}
                          aria-invalid={errors[`guest.${index}.idType`] ? "true" : undefined}
                          onChange={(event) => {
                            updateGuest(index, { idType: event.target.value });
                            setErrors((current) => ({ ...current, [`guest.${index}.idType`]: undefined }));
                          }}
                          onBlur={() =>
                            setErrors((current) => ({
                              ...current,
                              [`guest.${index}.idType`]: guest.idType ? undefined : "ID type is required.",
                            }))
                          }
                        >
                          <option value="">Select</option>
                          {idTypeOptions.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                        {errors[`guest.${index}.idType`] ? (
                          <span className="field-error">{errors[`guest.${index}.idType`]}</span>
                        ) : null}
                      </label>
                      <label>
                        ID Number<span style={{ color: "#dc2626" }}>*</span>
                        <input
                          placeholder="ID number"
                          aria-invalid={errors[`guest.${index}.idNumber`] ? "true" : undefined}
                          value={guest.idNumber || ""}
                          onChange={(event) => {
                            updateGuest(index, { idNumber: event.target.value });
                            setErrors((current) => ({ ...current, [`guest.${index}.idNumber`]: undefined }));
                          }}
                          onBlur={() =>
                            setErrors((current) => ({
                              ...current,
                              [`guest.${index}.idNumber`]: guest.idNumber?.trim() ? undefined : "ID number is required.",
                            }))
                          }
                        />
                        {errors[`guest.${index}.idNumber`] ? (
                          <span className="field-error">{errors[`guest.${index}.idNumber`]}</span>
                        ) : null}
                      </label>
                      <div className="guest-attachment">
                        <span className="guest-attachment-label">ID Proof<span style={{ color: "#dc2626" }}>*</span></span>
                        <div className="guest-attachment-picker">
                          <label className="secondary attachment-button" title={guest.attachmentName || "Choose a file"}>
                            <Paperclip size={15} />
                            <span className="attachment-name">{guest.attachmentName || "Choose file"}</span>
                            <input
                              type="file"
                              accept={GUEST_PROOF_ACCEPT}
                              hidden
                              onChange={(event) => {
                                attachGuest(index, event.target.files);
                                setErrors((current) => ({ ...current, [`guest.${index}.attachment`]: undefined }));
                              }}
                            />
                          </label>
                          {guest.attachment ? (
                            <button type="button" className="row-icon-btn is-danger" title="Remove attachment" onClick={() => updateGuest(index, { attachment: null, attachmentName: "" })}>
                              <X size={14} />
                            </button>
                          ) : null}
                        </div>
                        {errors[`guest.${index}.attachment`] ? (
                          <span className="field-error">{errors[`guest.${index}.attachment`]}</span>
                        ) : null}
                      </div>
                      </div>
                      <div className="guest-row-actions">
                        <button
                          type="button"
                          className="row-icon-btn"
                          title="Add guest"
                          disabled={(draft.id ? existingGuests.filter((item) => !isPrimaryGuest(item)).length : 0) + draft.guests.length >= additionalGuestLimit(draft.adults)}
                          onClick={addGuest}
                        >
                          <Plus size={15} />
                        </button>
                        <button
                          type="button"
                          className="row-icon-btn is-danger"
                          title="Remove guest"
                          disabled={!draft.id && draft.guests.length <= 1}
                          onClick={() => removeGuest(index)}
                        >
                          <Minus size={15} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="wide booking-section">
              <h3 className="booking-section-title" style={{ color: "#16a34a" }}>Payment and Accounting</h3>
              <div className="form-grid payment-fields">
                <label>
                  Agreed Price
                  <input type="number" value={draft.agreedPrice || 0} onChange={(event) => updateNumberField("agreedPrice", event.target.value)} />
                </label>
                <label>
                  Total Calculated
                  <input type="text" value={money(draft.totalAmount)} readOnly />
                </label>
                <label>
                  Advance Paid
                  <input type="number" value={draft.advancePaid || 0} onChange={(event) => updateNumberField("advancePaid", event.target.value)} />
                </label>
                <label>
                  Remaining Balance
                  <input type="number" value={draft.remainingBalance || 0} readOnly />
                </label>
                <label>
                  Payment Mode
                  <select
                    value={draft.paymentMode || "UPI"}
                    onChange={(event) => setDraft({ ...draft, paymentMode: event.target.value })}
                  >
                    <option value="UPI">UPI</option>
                    <option value="QR Code">QR Code</option>
                    <option value="Cash">Cash</option>
                    <option value="Debit/Credit Card">Debit/Credit Card</option>
                    <option value="Net Banking">Net Banking</option>
                  </select>
                </label>
                <label>
                  Booked By
                  <input placeholder="Booked By" value={username || draft.bookedBy || ""} readOnly />
                </label>
              </div>
            </section>

            <label className="status-field">
              Status
              <select
                value={draft.status || "On-hold"}
                onChange={(event) => setDraft({ ...draft, status: event.target.value })}
              >
                <option value="Booked">Booked</option>
                <option value="On-hold">On-hold</option>
                <option value="Checked-in">Check-in</option>
                <option value="Checked-Out">Check-out</option>
              </select>
            </label>
          </form>
        </Modal>
      )}
      {confirmationMessage && (
        <Modal
          title="Confirm Booking Status"
          onClose={() => setConfirmationMessage("")}
          footer={
            <div className="booking-confirmation-actions">
              <button type="button" className="secondary" onClick={() => setConfirmationMessage("")}>Cancel</button>
              <button type="button" className="primary" onClick={() => saveBooking(null, true)}>Confirm</button>
            </div>
          }
        >
          <p>{confirmationMessage}</p>
        </Modal>
      )}
    </section>
  );
}
